import React from 'react';
import { CURRENCY_SYMBOL, PillKind, t } from '../../components/list';

/** One card attempt of a payment (dbo.card_payments), as find-one-transaction returns it. */
export interface CardPaymentView {
  id: string;
  paymentProvider: number | null;
  providerPaymentId: string | null;
  sessionId: string | null;
  terminalId: string | null;
  tid: string | null;
  reference: string | null;
  providerTransactionId: string | null;
  currency: string | null;
  amount: number;
  tip: number;
  totalAmount: number;
  refundedAmount: number;
  paymentStatus: string | null;
  responseStatus: string | null;
  authCode: string | null;
  cardType: string | null;
  fundingType: string | null;
  lastFour: string | null;
  entryMode: string | null;
  verificationMethod: string | null;
  capturedAt: string | null;
  createdAt: string | null;
}

export interface SalePayment {
  id: string;
  paymentAmount?: number | string | null;
  paymentDate?: string | null;
  createdAt?: string | null;
  isVoided?: boolean;
  deviceTender?: { tenderName?: string | null; tenderType?: number | null } | null;
  cardPayments?: CardPaymentView[];
}

export const INK = '#05292A';
export const SURFACE_SOFT = '#F9FBFB';

/** Tender types as dbo.device_tenders numbers them; labels are the DeviceTender page's i18n keys. */
export const TENDER_TYPE_LABELS: Record<number, string> = {
  1: 'Cash',
  2: 'Card',
  3: 'Promotion',
  4: 'Voucher',
  5: 'OnAccount',
};

/* ---------------- Numbers, money and dates ---------------- */

/** NUMERIC columns arrive as strings; anything unparsable counts as 0. */
export const toNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const symbols = new Map<string, string>();

/** "£" for GBP; the app's € convention when the code is unknown; the raw code when Intl cannot name it. */
export const currencySymbol = (code?: string | null): string => {
  const c = String(code ?? '').trim().toUpperCase();
  if (!c) return CURRENCY_SYMBOL;
  if (!symbols.has(c)) {
    let symbol = c;
    try {
      symbol =
        new Intl.NumberFormat('en', { style: 'currency', currency: c, currencyDisplay: 'narrowSymbol' })
          .formatToParts(0)
          .find(part => part.type === 'currency')?.value ?? c;
    } catch {
      symbol = c;
    }
    symbols.set(c, symbol);
  }
  return symbols.get(c) as string;
};

/** "€ 11.50", the list kit's format; pass a card's currency code to show that instead. */
export const money = (value: unknown, currency?: string | null): string =>
  `${currencySymbol(currency)} ${toNumber(value).toFixed(2)}`;

/** Quantities lose trailing zeros ("2.000" reads 2). */
export const quantity = (value: unknown): string => String(Math.round(toNumber(value) * 1000) / 1000);

export const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  const d = new Date(value as any);
  return Number.isNaN(d.getTime()) ? null : d;
};

const pad = (n: number) => String(n).padStart(2, '0');

/** dd.MM.yyyy, the app's day format. */
export const formatDay = (d: Date): string => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;

export const formatClock = (d: Date, withSeconds = false): string =>
  `${pad(d.getHours())}:${pad(d.getMinutes())}${withSeconds ? `:${pad(d.getSeconds())}` : ''}`;

/* ---------------- Card attempt values ---------------- */

/** "SUCCESSFUL" / "chip_and_pin" read "Successful" / "Chip and pin"; mixed case and short codes (PIN) stay as sent. */
export const humanize = (value?: string | null): string => {
  const raw = String(value ?? '').trim();
  if (!raw || raw.length <= 3) return raw;
  const spaced = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (spaced === raw && /[a-z]/.test(raw) && /[A-Z]/.test(raw)) return raw;
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/** Pill colour of a provider status: failures red (negations like UNSUCCESSFUL first), approvals green, else neutral. */
export const statusTone = (value?: string | null): PillKind => {
  const v = String(value ?? '').toLowerCase();
  if (!v) return 'off';
  if (/(^|[^a-z])(un|not[ _-]?)(success|authori|approv|paid|complet|accept)|incomplet/.test(v)) return 'danger';
  if (/declin|fail|error|reject|cancel|revers|void|expire|time[ _-]?out|timed[ _-]?out|refus|abort|denied/.test(v)) return 'danger';
  if (/success|captur|approv|authori[sz]|complet|accept|paid/.test(v)) return 'ok';
  return 'off';
};

export const isApproved = (attempt: CardPaymentView): boolean => {
  const payment = statusTone(attempt.paymentStatus);
  return payment === 'ok' || (payment !== 'danger' && statusTone(attempt.responseStatus) === 'ok');
};

const timeOf = (value: unknown) => toDate(value)?.getTime() ?? 0;

/** Attempts in the order they happened: by createdAt, then uncaptured first (a capture ends the payment). */
export const attemptsInOrder = (attempts?: CardPaymentView[] | null): CardPaymentView[] =>
  [...(attempts ?? [])].sort(
    (a, b) =>
      timeOf(a.createdAt) - timeOf(b.createdAt) ||
      Number(!!a.capturedAt) - Number(!!b.capturedAt) ||
      timeOf(a.capturedAt) - timeOf(b.capturedAt)
  );

/** The attempt a payment row summarises: the last approved one, else the last one. */
export const primaryAttempt = (attempts: CardPaymentView[]): CardPaymentView | null => {
  const ordered = attemptsInOrder(attempts);
  const approved = ordered.filter(isApproved);
  const pool = approved.length ? approved : ordered;
  return pool.length ? pool[pool.length - 1] : null;
};

/** "ending in 5273" for screen readers, where "•••• 5273" would be read as bullets. */
export const endingIn = (lastFour: string | null | undefined, language: any): string =>
  lastFour ? `${t(language, 'ending in', 'ending in')} ${lastFour}` : '';

export const isContactless = (entryMode?: string | null): boolean =>
  /contactless|nfc|tap|wave/i.test(String(entryMode ?? ''));

export type BrandKey = 'visa' | 'mastercard' | 'maestro' | 'amex' | 'other';

export interface CardBrand {
  key: BrandKey;
  /** Display name; empty for an unknown network (callers show "Card"). */
  name: string;
  background: string;
}

const TEAL_CARD = 'linear-gradient(135deg, #0F7679 0%, #0A4C4E 100%)';

export const cardBrandOf = (cardType?: string | null): CardBrand => {
  const v = String(cardType ?? '').toLowerCase().replace(/[^a-z]/g, '');
  if (v.includes('visa')) return { key: 'visa', name: 'Visa', background: 'linear-gradient(135deg, #1A1F71 0%, #2B3BA6 100%)' };
  if (v.includes('maestro')) return { key: 'maestro', name: 'Maestro', background: 'linear-gradient(135deg, #1C2430 0%, #34404F 100%)' };
  if (v.includes('master') || v === 'mc') {
    return { key: 'mastercard', name: 'Mastercard', background: 'linear-gradient(135deg, #1D2327 0%, #3A454E 100%)' };
  }
  if (v.includes('amex') || v.includes('americanexpress')) {
    return { key: 'amex', name: 'Amex', background: 'linear-gradient(135deg, #1F6FB5 0%, #134A80 100%)' };
  }
  return { key: 'other', name: humanize(cardType), background: TEAL_CARD };
};

/* ---------------- Small visuals ---------------- */

const OverlapCircles: React.FC<{ left: string; right: string; size: number }> = ({ left, right, size }) => (
  <svg width={size * 1.6} height={size} viewBox="0 0 32 20" aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
    <circle cx="10" cy="10" r="10" fill={left} />
    <circle cx="22" cy="10" r="10" fill={right} fillOpacity="0.9" />
  </svg>
);

/** The network mark on the card visual: styled type, never a logo image. */
export const BrandMark: React.FC<{ brand: CardBrand; fallback: string }> = ({ brand, fallback }) => {
  if (brand.key === 'visa') {
    return (
      <span style={{ fontStyle: 'italic', fontWeight: 800, fontSize: '22px', letterSpacing: '0.04em', lineHeight: 1 }}>
        VISA
      </span>
    );
  }
  if (brand.key === 'mastercard' || brand.key === 'maestro') {
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
        <OverlapCircles left="#EB001B" right={brand.key === 'maestro' ? '#00A2E5' : '#F79E1B'} size={22} />
        <span style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.02em', lineHeight: 1 }}>
          {brand.key === 'maestro' ? 'maestro' : 'mastercard'}
        </span>
      </span>
    );
  }
  if (brand.key === 'amex') {
    return (
      <span
        style={{
          fontWeight: 800,
          fontSize: '14px',
          letterSpacing: '0.08em',
          lineHeight: 1,
          padding: '4px 6px',
          border: '1.5px solid rgba(255,255,255,.85)',
          borderRadius: '4px',
        }}
      >
        AMEX
      </span>
    );
  }
  return (
    <span style={{ fontWeight: 700, fontSize: '15px', letterSpacing: '0.02em', lineHeight: 1 }}>
      {brand.name || fallback}
    </span>
  );
};

/** 28x18 card swatch in the brand colour, beside "Visa •••• 5273" in a payment row. */
export const MiniCard: React.FC<{ brand: CardBrand }> = ({ brand }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      width: '28px',
      height: '18px',
      borderRadius: '4px',
      background: brand.background,
      padding: '2px 3px',
      boxSizing: 'border-box',
      flex: 'none',
    }}
  >
    {brand.key === 'mastercard' || brand.key === 'maestro' ? (
      <OverlapCircles left="#EB001B" right={brand.key === 'maestro' ? '#00A2E5' : '#F79E1B'} size={7} />
    ) : (
      <span style={{ width: '9px', height: '3px', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,.8)' }} />
    )}
  </span>
);

/** Contactless indicator (four arcs). */
export const ContactlessMark: React.FC<{ color?: string; size?: number }> = ({ color = '#FFFFFF', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <path d="M6.5 9.5a4 4 0 0 1 0 5" />
    <path d="M10 7.5a7 7 0 0 1 0 9" />
    <path d="M13.5 5.5a10 10 0 0 1 0 13" />
    <path d="M17 3.5a13 13 0 0 1 0 17" />
  </svg>
);

const TENDER_PATHS: Record<number, React.ReactNode> = {
  1: (
    <>
      <rect x="2.5" y="5.5" width="15" height="9" rx="1.5" />
      <circle cx="10" cy="10" r="2" />
      <path d="M5 8v4M15 8v4" />
    </>
  ),
  2: (
    <>
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <path d="M2.5 8h15M5.5 12.5h3" />
    </>
  ),
  3: (
    <>
      <path d="M3 10.2V4h6.2l7.3 7.3-6.2 6.2z" />
      <circle cx="6.5" cy="7.5" r="1.1" />
    </>
  ),
  4: (
    <>
      <path d="M2.5 6.5h15v2a1.5 1.5 0 0 0 0 3v2h-15v-2a1.5 1.5 0 0 0 0-3z" />
      <path d="M12 6.5v7" strokeDasharray="1.5 1.5" />
    </>
  ),
  5: (
    <>
      <rect x="4" y="2.5" width="12" height="15" rx="1.5" />
      <path d="M7 6.5h6M7 9.5h6M7 12.5h3.5" />
    </>
  ),
};

const TENDER_TONES: Record<number, { bg: string; fg: string }> = {
  1: { bg: '#E4F1EA', fg: '#1F6B4A' },
  2: { bg: '#E7F1F1', fg: '#0F7679' },
  3: { bg: '#FBEFE3', fg: '#9A5B13' },
  4: { bg: '#EEEAF8', fg: '#5B4A9E' },
  5: { bg: '#EBF0F0', fg: '#4A5353' },
};

/** 36px tender avatar: the type's icon, or the tender name's initial for an unknown type. */
export const TenderAvatar: React.FC<{ type?: number | null; name: string }> = ({ type, name }) => {
  const known = type !== null && type !== undefined && !!TENDER_PATHS[type];
  const tone = known ? TENDER_TONES[type as number] : { bg: '#EBF0F0', fg: '#4A5353' };
  return (
    <span
      aria-hidden="true"
      style={{
        width: '36px',
        height: '36px',
        borderRadius: '10px',
        backgroundColor: tone.bg,
        color: tone.fg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        fontSize: '15px',
        fontWeight: 600,
      }}
    >
      {known ? (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke={tone.fg} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          {TENDER_PATHS[type as number]}
        </svg>
      ) : (
        (name.trim().charAt(0) || '?').toUpperCase()
      )}
    </span>
  );
};
